import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  Breadcrumbs,
} from '@mui/material';
import {
  collection,
  addDoc,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

import styles from './styles';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';

// Tipagem da sugestão de endereço retornada pela API
interface AddressSuggestion {
  street: string;
  neighborhood: string;
  city: string;
  uf: string;
}

const AddAddress: React.FC = () => {
  const auth = getAuth();
  const db = getFirestore();

  const [form, setForm] = useState({
    recipient: '',
    zip: '',
    number: '',
    complement: '',
    reference: '',
  });
  const [suggestion, setSuggestion] = useState<AddressSuggestion | null>(null);

  // Estados de erro para validação de CEP e salvamento
  const [zipError, setZipError] = useState(false);
  const [zipErrorText, setZipErrorText] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const navigate = useNavigate();

  // Valida e busca endereço ao alterar CEP
  useEffect(() => {
    const cepDigits = form.zip.replace(/\D/g, '');
    if (cepDigits.length === 0) {
      setZipError(false);
      setZipErrorText('');
      setSuggestion(null);
    } else if (cepDigits.length < 8) {
      setZipError(true);
      setZipErrorText('CEP incompleto');
      setSuggestion(null);
    } else if (cepDigits.length === 8) {
      fetchAddress(cepDigits);
    } else {
      setZipError(true);
      setZipErrorText('Formato de CEP inválido');
      setSuggestion(null);
    }
  }, [form.zip]);

  // Função que chama a API ViaCEP
  const fetchAddress = async (cep: string) => {
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setZipError(true);
        setZipErrorText('CEP não encontrado');
        setSuggestion(null);
      } else {
        setZipError(false);
        setZipErrorText('');
        setSuggestion({
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade,
          uf: data.uf || '',
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setZipError(true);
      setZipErrorText('Erro ao buscar CEP');
      setSuggestion(null);
    }
  };

  // Atualiza estado do formulário
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Salva no Firestore em subcoleção 'addresses' do usuário
  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess('');

    // Validações básicas
    if (!form.recipient) {
      setSaveError('Informe o nome do destinatário');
      return;
    }
    if (zipError || form.zip.replace(/\D/g, '').length !== 8) {
      setSaveError('Corrija o CEP antes de salvar');
      return;
    }
    if (!suggestion) {
      setSaveError('Aguardando sugestão de endereço');
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Usuário não autenticado');
      // Estrutura: users/{uid}/addresses/{addressId}
      await addDoc(
        collection(db, 'subscription_users', user.uid, 'addresses'),
        {
          recipient: form.recipient,
          zip: form.zip,
          number: form.number,
          complement: form.complement,
          reference: form.reference,
          ...suggestion,
          createdAt: serverTimestamp(),
        },
      );

      setSaveSuccess('Endereço salvo com sucesso!');
      // Limpa formulário
      setForm({
        recipient: '',
        zip: '',
        number: '',
        complement: '',
        reference: '',
      });
      setSuggestion(null);
      navigate('/addresses');
    } catch (err: any) {
      console.error('Erro ao salvar endereço:', err);
      setSaveError('Falha ao salvar: ' + (err.message || 'erro desconhecido'));
    }
  };

  const handleBack = useCallback((): void => {
    navigate('/addresses');
  }, [navigate]);
  return (
    <Box sx={styles.container}>
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={styles.breadcrumbs}
      >
        <Link underline="hover" sx={styles.link} onClick={handleBack}>
          Meus endereços
        </Link>
        <Typography sx={styles.breadCurrent}>Adicionar endereços</Typography>
      </Breadcrumbs>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        CADASTRAR NOVO ENDEREÇO
      </Typography>
      <Grid container spacing={2}>
        {/* ... campos ... */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="recipient"
            label="Nome do destinatário"
            variant="outlined"
            value={form.recipient}
            onChange={handleChange}
            sx={styles.textField}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="zip"
            label="CEP"
            variant="outlined"
            value={form.zip}
            onChange={handleChange}
            sx={styles.textField}
            error={zipError}
            helperText={zipErrorText}
            InputProps={{ sx: { fontWeight: 'bold' } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="number"
            label="Número"
            variant="outlined"
            value={form.number}
            onChange={handleChange}
            sx={styles.textField}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="complement"
            label="Complemento"
            variant="outlined"
            value={form.complement}
            onChange={handleChange}
            sx={styles.textField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="reference"
            label="Referência"
            variant="outlined"
            value={form.reference}
            onChange={handleChange}
            sx={styles.textField}
          />
        </Grid>
        {/* Sugestão de endereço */}
        {suggestion && (
          <Grid item xs={12}>
            <Paper sx={styles.suggestionBox} elevation={0}>
              <Typography>{suggestion.street}</Typography>
              <Typography>{suggestion.neighborhood}</Typography>
              <Typography>
                {suggestion.city} - {suggestion.uf}
              </Typography>
            </Paper>
          </Grid>
        )}
        {/* Mensagens de erro e sucesso */}
        {saveError && (
          <Grid item xs={12}>
            <Alert severity="error">{saveError}</Alert>
          </Grid>
        )}
        {saveSuccess && (
          <Grid item xs={12}>
            <Alert severity="success">{saveSuccess}</Alert>
          </Grid>
        )}
        {/* Botão de salvar */}
        <Grid item xs={12}>
          <Box sx={styles.buttonBox}>
            {/* <Button
              variant="contained"
              size="large"
              onClick={handleBack}
              sx={styles.saveButton}
            >
              Voltar
            </Button> */}
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              sx={styles.saveButton}
            >
              SALVAR ENDEREÇO
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddAddress;
